import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  User, 
  Package, 
  Send, 
  RotateCcw,
  Calendar,
  Phone,
  Loader2,
  SendHorizontal,
  FileText
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useOnHold, OnHoldItemWithDetails } from '@/hooks/use-onhold';
import { useProducts } from '@/hooks/use-products';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { Database } from '@/integrations/supabase/types';
import { productService } from '@/lib/packages';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type ProductRow = Database['public']['Tables']['products']['Row'];

export default function OnHoldSale() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState<OnHoldItemWithDetails | null>(null);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [replacementProduct, setReplacementProduct] = useState<ProductRow | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmItem, setConfirmItem] = useState<OnHoldItemWithDetails | null>(null);
  const [sendingAllCustomerId, setSendingAllCustomerId] = useState<string | null>(null);
  const [sendingItemId, setSendingItemId] = useState<string | null>(null);
  
  // Product search for replacement
  const [replacementProductSearchTerm, setReplacementProductSearchTerm] = useState('');
  const [replacementProductSearchResults, setReplacementProductSearchResults] = useState<ProductRow[]>([]);
  const [replacementProductSearchLoading, setReplacementProductSearchLoading] = useState(false);
  const [replacementProductSearchHasMore, setReplacementProductSearchHasMore] = useState(false);
  const [replacementProductSearchOffset, setReplacementProductSearchOffset] = useState(0);
  const [replacementProductSearchLimit] = useState(20); // Initial limit
  const replacementProductSearchResultsRef = useRef<ProductRow[]>([]); // Ref to track current results
  
  // Hooks
  const { 
    onHoldItems, 
    loading: onHoldLoading, 
    fetchOnHoldItems, 
    convertToSale, 
    replaceProduct,
    replaceAndSendProduct 
  } = useOnHold();
  
  const { 
    products, 
    loading: productsLoading, 
    fetchProducts 
  } = useProducts();
  
  const { companyId } = useAuth();
  const { currentLocation } = useLocation();
  const effectiveLocationId = currentLocation?.id || null;

  // Load data on component mount
  useEffect(() => {
    if (companyId) {
      fetchOnHoldItems({ 
        searchTerm, 
        companyId,
        locationId: effectiveLocationId || undefined,
      });
    }
  }, [companyId, searchTerm, effectiveLocationId, fetchOnHoldItems]);


  const filteredItems = onHoldItems.filter(item => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const productName = item.product?.name?.toLowerCase() || '';
    const productCode = item.product?.item_code?.toLowerCase() || '';
    const customerName = `${item.customer?.first_name || ''} ${item.customer?.last_name || ''}`.toLowerCase();
    const customerPhone = item.customer?.phone || '';
    
    return productName.includes(searchLower) ||
           productCode.includes(searchLower) ||
           customerName.includes(searchLower) ||
           customerPhone.includes(searchTerm);
  });

  // Group items by customer
  const groupedByCustomer = filteredItems.reduce((acc, item) => {
    const customerId = item.customer_id || 'unknown';
    if (!acc[customerId]) {
      acc[customerId] = {
        customer: item.customer,
        items: []
      };
    }
    acc[customerId].items.push(item);
    return acc;
  }, {} as Record<string, { customer: OnHoldItemWithDetails['customer']; items: OnHoldItemWithDetails[] }>);

  const handleSendToSale = (item: OnHoldItemWithDetails) => {
    setConfirmItem(item);
    setShowConfirmDialog(true);
  };

  const confirmSendToSale = async () => {
    if (!confirmItem) return;
    
    setSendingItemId(confirmItem.id);
    const success = await convertToSale(confirmItem.id);
    if (success && companyId) {
      // Refresh the on-hold items list
      fetchOnHoldItems({ 
        searchTerm, 
        companyId,
        locationId: effectiveLocationId || undefined,
      });
    }
    
    setShowConfirmDialog(false);
    setConfirmItem(null);
    setSendingItemId(null);
  };

  const handleSendAllForCustomer = async (customerId: string, items: OnHoldItemWithDetails[]) => {
    setSendingAllCustomerId(customerId);
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const item of items) {
        const success = await convertToSale(item.id);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      if (companyId) {
        // Refresh the on-hold items list
        fetchOnHoldItems({ 
          searchTerm, 
          companyId,
          locationId: effectiveLocationId || undefined,
        });
      }
      
      if (failCount === 0) {
        toast({
          title: "Başarılı",
          description: `${successCount} ürün başarıyla satışa dönüştürüldü`,
        });
      } else {
        toast({
          title: "Kısmen Başarılı",
          description: `${successCount} ürün başarıyla gönderildi, ${failCount} ürün gönderilemedi`,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: "Toplu gönderme işlemi sırasında bir hata oluştu",
        variant: "destructive",
      });
    } finally {
      setSendingAllCustomerId(null);
    }
  };

  // Search replacement products with pagination
  const searchReplacementProducts = useCallback(async (searchTerm: string, loadMore: boolean = false) => {
    if (!companyId || searchTerm.length < 2) {
      setReplacementProductSearchResults([]);
      replacementProductSearchResultsRef.current = [];
      setReplacementProductSearchHasMore(false);
      setReplacementProductSearchOffset(0);
      return;
    }

    setReplacementProductSearchLoading(true);
    try {
      // Use productService.searchProducts which handles pagination internally
      // This searches within the company's products with pagination support
      let currentLimit = replacementProductSearchLimit;
      
      if (loadMore) {
        // When loading more, use ref to get current length
        currentLimit = replacementProductSearchResultsRef.current.length + replacementProductSearchLimit;
      }
      
      // Fetch products - productService handles pagination internally and searches within company products
      const results = await productService.searchProducts(companyId, searchTerm, currentLimit);
      
      if (loadMore) {
        // Append new results, avoiding duplicates
        const existingIds = new Set(replacementProductSearchResultsRef.current.map(p => p.id));
        const newResults = results.filter(p => !existingIds.has(p.id));
        const updatedResults = [...replacementProductSearchResultsRef.current, ...newResults];
        setReplacementProductSearchResults(updatedResults);
        replacementProductSearchResultsRef.current = updatedResults;
      } else {
        // Replace results for new search
        setReplacementProductSearchResults(results);
        replacementProductSearchResultsRef.current = results;
        setReplacementProductSearchOffset(0);
      }
      
      // Check if there might be more results
      setReplacementProductSearchHasMore(results.length >= currentLimit);
    } catch (error) {
      console.error('Error searching replacement products:', error);
      toast({
        title: "Hata",
        description: "Ürünler aranırken bir hata oluştu",
        variant: "destructive",
      });
      setReplacementProductSearchResults([]);
      replacementProductSearchResultsRef.current = [];
      setReplacementProductSearchHasMore(false);
    } finally {
      setReplacementProductSearchLoading(false);
    }
  }, [companyId, replacementProductSearchLimit, toast]);
  
  // Update ref when results change
  useEffect(() => {
    replacementProductSearchResultsRef.current = replacementProductSearchResults;
  }, [replacementProductSearchResults]);

  // Handle search term changes with debouncing
  useEffect(() => {
    if (!showReplaceDialog) {
      // Reset search when dialog closes
      setReplacementProductSearchTerm('');
      setReplacementProductSearchResults([]);
      setReplacementProductSearchHasMore(false);
      setReplacementProductSearchOffset(0);
      return;
    }

    const timeoutId = setTimeout(() => {
      if (replacementProductSearchTerm.length >= 2) {
        searchReplacementProducts(replacementProductSearchTerm, false);
      } else {
        setReplacementProductSearchResults([]);
        setReplacementProductSearchHasMore(false);
      }
    }, 300); // Debounce for 300ms

    return () => clearTimeout(timeoutId);
  }, [replacementProductSearchTerm, showReplaceDialog, searchReplacementProducts]);

  const handleReplaceProduct = async () => {
    if (!selectedItem || !replacementProduct) return;

    // Check if replacement product has enough stock
    if (replacementProduct.quantity_on_hand < selectedItem.hold_quantity) {
      toast({
        title: "Yetersiz Stok",
        description: `${replacementProduct.name} için yeterli stok bulunmuyor`,
        variant: "destructive",
      });
      return;
    }

    const success = await replaceAndSendProduct(selectedItem.id, replacementProduct.id);
    if (success && companyId) {
      // Refresh the on-hold items list
      fetchOnHoldItems({ 
        searchTerm, 
        companyId,
        locationId: effectiveLocationId || undefined,
      });
      
      setSelectedItem(null);
      setReplacementProduct(null);
      setShowReplaceDialog(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Beklemedeki Satışlar</h1>
            <p className="text-muted-foreground">
              Beklemedeki ürünleri yönetin, satışa dönüştürün veya ürün değiştirin
            </p>
          </div>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="secondary" 
                  onClick={() => navigate('/onhold-report')}
                  className="gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 font-medium"
                >
                  <FileText className="w-4 h-4" />
                  Tüm Beklemeler
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  Tüm beklemedeki ürünlerin detaylı raporunu görüntüleyin, filtreleyin ve yönetin
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Arama
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="search">Ürün, müşteri veya telefon ile ara</Label>
                <Input
                  id="search"
                  placeholder="Ürün adı, kodu, müşteri adı veya telefon..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results - Grouped by Customer */}
        <div className="space-y-6">
          {Object.keys(groupedByCustomer).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-medium mb-2">Beklemedeki ürün bulunamadı</h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm ? 'Arama kriterlerinizi değiştirmeyi deneyin' : 'Henüz beklemedeki ürün bulunmuyor'}
                </p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(groupedByCustomer).map(([customerId, { customer, items }]) => {
              const customerTotal = items.reduce((sum, item) => {
                const price = (item as any).effective_price || item.hold_price || 0;
                const quantity = (item as any).effective_quantity || item.hold_quantity || 0;
                return sum + (price * quantity);
              }, 0);
              const isSendingAll = sendingAllCustomerId === customerId;
              
              return (
                <Card key={customerId} className="overflow-hidden">
                  {/* Customer Header */}
                  <CardHeader className="bg-muted/50 border-b">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-xl">
                            {`${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() || 'Müşteri bulunamadı'}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              {customer?.phone || 'Telefon yok'}
                            </p>
                            <Badge variant="secondary" className="ml-2">
                              {items.length} {items.length === 1 ? 'ürün' : 'ürün'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Toplam Tutar</p>
                          <p className="text-lg font-bold text-green-600">
                            ₺{customerTotal.toLocaleString('tr-TR')}
                          </p>
                        </div>
                        <Button
                          onClick={() => handleSendAllForCustomer(customerId, items)}
                          disabled={isSendingAll || onHoldLoading}
                          className="gap-2 bg-blue-600 hover:bg-blue-700"
                          size="lg"
                        >
                          {isSendingAll ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Gönderiliyor...
                            </>
                          ) : (
                            <>
                              <SendHorizontal className="w-4 h-4" />
                              Tümünü Gönder
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {/* Customer Items */}
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {items.map((item) => {
                        const isSendingItem = sendingItemId === item.id;
                        return (
                          <div key={item.id} className="p-6 hover:bg-muted/30 transition-colors">
                            <div className="flex flex-col lg:flex-row gap-6">
                              <div className="flex-1 space-y-4">
                                {/* Product Header */}
                                <div className="flex items-start justify-between">
                                  <div>
                                    <h3 className="font-semibold text-lg">{item.product?.name || 'Ürün bulunamadı'}</h3>
                                    <p className="text-sm text-muted-foreground">{item.product?.item_code}</p>
                                  </div>
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                    Beklemede
                                  </Badge>
                                </div>

                                {/* Product Details */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div>
                                    <Label className="text-xs font-medium text-muted-foreground">MİKTAR</Label>
                                    <p className="font-medium text-base">{(item as any).effective_quantity || item.hold_quantity} adet</p>
                                  </div>
                                  <div>
                                    <Label className="text-xs font-medium text-muted-foreground">BİRİM FİYAT</Label>
                                    <p className="font-medium text-base text-green-600">
                                      ₺{((item as any).effective_price || item.hold_price || 0).toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-xs font-medium text-muted-foreground">TOPLAM TUTAR</Label>
                                    <p className="font-semibold text-base text-green-700">
                                      ₺{(((item as any).effective_price || item.hold_price || 0) * ((item as any).effective_quantity || item.hold_quantity || 0)).toLocaleString('tr-TR')}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-xs font-medium text-muted-foreground">BEKLEME TARİHİ</Label>
                                    <p className="font-medium text-sm flex items-center gap-1">
                                      <Calendar className="w-3 h-3 text-muted-foreground" />
                                      {new Date(item.date_on_hold).toLocaleDateString('tr-TR')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex flex-row lg:flex-col gap-2 lg:w-48">
                                <Button 
                                  onClick={() => handleSendToSale(item)} 
                                  className="gap-2 bg-green-600 hover:bg-green-700 flex-1 lg:flex-none"
                                  size="lg"
                                  disabled={isSendingItem || isSendingAll || onHoldLoading}
                                >
                                  {isSendingItem ? (
                                    <>
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Gönderiliyor...
                                    </>
                                  ) : (
                                    <>
                                      <Send className="w-4 h-4" />
                                      Gönder
                                    </>
                                  )}
                                </Button>
                                <Button 
                                  variant="outline" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Set both states together - React will batch them
                                    setSelectedItem(item);
                                    setShowReplaceDialog(true);
                                  }}
                                  className="gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50 flex-1 lg:flex-none text-xs"
                                  size="sm"
                                  disabled={isSendingAll || onHoldLoading || showReplaceDialog}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                  Muadil Gönder
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Confirmation Dialog for Send to Sale */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Satışa Dönüştür</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmItem && (
                  <div className="space-y-3 mt-4">
                    <p>Bu işlem ile beklemedeki ürün satışa dönüştürülecek ve stoktan düşürülecektir:</p>
                    
                    <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span className="font-medium">{confirmItem.product?.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{`${confirmItem.customer?.first_name || ''} ${confirmItem.customer?.last_name || ''}`.trim()}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t text-sm">
                        <div>
                          <span className="text-muted-foreground">Miktar:</span>
                          <br />
                          <span className="font-medium">{(confirmItem as any).effective_quantity || confirmItem.hold_quantity} adet</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Birim Fiyat:</span>
                          <br />
                          <span className="font-medium">₺{((confirmItem as any).effective_price || confirmItem.hold_price || 0).toLocaleString('tr-TR')}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Toplam:</span>
                          <br />
                          <span className="font-medium text-green-600">
                            ₺{(((confirmItem as any).effective_price || confirmItem.hold_price || 0) * ((confirmItem as any).effective_quantity || confirmItem.hold_quantity || 0)).toLocaleString('tr-TR')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      Bu işlem geri alınamaz. Devam etmek istediğinizden emin misiniz?
                    </p>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={confirmSendToSale} disabled={onHoldLoading}>
                {onHoldLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Satışa Dönüştür
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Replace Product Dialog */}
        <Dialog 
          open={showReplaceDialog} 
          onOpenChange={(open) => {
            // Prevent closing during loading
            if (!open && (onHoldLoading || productsLoading)) {
              return;
            }
            if (!open) {
              // Reset states when closing
              setReplacementProduct(null);
              setSelectedItem(null);
            }
            setShowReplaceDialog(open);
          }}
        >
          <DialogContent 
            className="max-w-4xl max-h-[90vh]"
            onInteractOutside={(e) => {
              // Prevent closing when clicking outside during loading
              if (onHoldLoading || productsLoading) {
                e.preventDefault();
              }
            }}
            onEscapeKeyDown={(e) => {
              // Prevent closing with Escape during loading
              if (onHoldLoading || productsLoading) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5" />
                Muadil Ürün Seç
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {selectedItem && (
                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Package className="w-8 h-8 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">Değiştirilecek ürün:</p>
                            <p className="font-semibold text-lg">{selectedItem.product?.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {selectedItem.product?.item_code} • Miktar: {selectedItem.hold_quantity} adet
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Birim Fiyat</p>
                            <p className="font-semibold text-lg text-green-600">
                              ₺{((selectedItem as any).effective_price || selectedItem.hold_price || 0).toLocaleString('tr-TR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <div className="space-y-4">
                <div>
                  <Label className="font-medium mb-2 block">Muadil Ürün Ara</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Ürün adı veya kodu ile ara..."
                      value={replacementProductSearchTerm}
                      onChange={(e) => setReplacementProductSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                    {replacementProductSearchLoading && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  {replacementProductSearchTerm.length > 0 && replacementProductSearchTerm.length < 2 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      En az 2 karakter girin
                    </p>
                  )}
                </div>
                
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  {replacementProductSearchLoading && replacementProductSearchResults.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        <p className="text-muted-foreground">Ürünler aranıyor...</p>
                      </div>
                    </div>
                  ) : replacementProductSearchTerm.length >= 2 && replacementProductSearchResults.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <Package className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                        <p className="font-medium">Ürün bulunamadı</p>
                        <p className="text-sm text-muted-foreground">
                          Arama kriterlerinizi değiştirmeyi deneyin
                        </p>
                      </div>
                    </div>
                  ) : replacementProductSearchTerm.length < 2 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <Search className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                        <p className="font-medium">Ürün aramak için yukarıdaki alana yazın</p>
                        <p className="text-sm text-muted-foreground">
                          En az 2 karakter girin
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
                        {replacementProductSearchResults.map((product) => (
                          <Card
                            key={product.id}
                            className={cn(
                              "cursor-pointer transition-all duration-200 hover:shadow-md border-2",
                              replacementProduct?.id === product.id 
                                ? "bg-primary/5 border-primary shadow-md" 
                                : "border-border hover:border-primary/50"
                            )}
                            onClick={() => setReplacementProduct(product)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <Package className="w-10 h-10 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-base line-clamp-2">{product.name}</h4>
                                      <p className="text-sm text-muted-foreground mt-1">
                                        {product.item_code}
                                      </p>
                                    </div>
                                    {replacementProduct?.id === product.id && (
                                      <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                                    <div>
                                      <p className="text-lg font-bold text-green-600">
                                        ₺{product.price.toLocaleString('tr-TR')}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        Stok: {product.quantity_on_hand} adet
                                      </p>
                                    </div>
                                    {product.quantity_on_hand < (selectedItem?.hold_quantity || 0) && (
                                      <Badge variant="destructive" className="text-xs">
                                        Yetersiz Stok
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      {replacementProductSearchHasMore && replacementProductSearchTerm.length >= 2 && (
                        <div className="p-4 border-t">
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              if (replacementProductSearchTerm.length >= 2) {
                                searchReplacementProducts(replacementProductSearchTerm, true);
                              }
                            }}
                            disabled={replacementProductSearchLoading}
                          >
                            {replacementProductSearchLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                Yükleniyor...
                              </>
                            ) : (
                              'Daha fazla yükle...'
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {replacementProduct && (
                  <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-sm font-medium">Seçili Ürün:</p>
                    <p className="text-base font-semibold">{replacementProduct.name}</p>
                    <p className="text-xs text-muted-foreground">{replacementProduct.item_code}</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowReplaceDialog(false);
                    setReplacementProduct(null);
                    setSelectedItem(null);
                  }}
                  disabled={onHoldLoading}
                >
                  İptal
                </Button>
                <Button 
                  className="flex-1" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleReplaceProduct();
                  }}
                  disabled={!replacementProduct || (replacementProduct.quantity_on_hand < (selectedItem?.hold_quantity || 0)) || onHoldLoading || productsLoading}
                >
                  {onHoldLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Değiştiriliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Muadil Ürünü Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}